let instanceIterator = 0;

export default class EventManager {
    constructor(options = {}) {
        // This this class can be used with a Vue based event bus, or just about any event system using a typical
        // API.
        this.options = Object.assign({
            add: 'addEventListener',
            remove: 'removeEventListener',
            // Allow automatic binding of functions passed as callbacks to an instance
            bind: null
        }, options);

        this.destroyed = false;

        this.events = {};
        this.elements = [];

        this.instanceId = ++instanceIterator;
        this.indexKey = '_Events_' + this.instanceId + '_index';
    }

    /**
     * Returns the index for a given element for this instance of EventManager.
     *
     * @param {HTMLElement} element
     * @returns {number}
     */
    getIndex(element) {
        if (typeof element[this.indexKey] !== 'number') {
            let elementIndex = this.elements.indexOf(element);

            if (elementIndex === -1) {
                elementIndex += this.elements.push(element);
            }

            element[this.indexKey] = elementIndex;
        }

        return element[this.indexKey];
    }

    /**
     * Sets an event listener on a given element.
     *
     * @param {*} element
     * @param {string} event
     * @param {function} callback
     */
    on(element, event, callback) {
        if (!element || typeof element[this.options.add] !== 'function') {
            throw new TypeError('Invalid element passed to Events.on');
        }

        if (typeof event !== 'string') {
            throw new TypeError('Invalid event name passed to Events.on');
        }

        let elementIndex = this.getIndex(element),
            listenerCallback = callback;
        
        // Should the listener be bound before being set?
        if (this.options.bind) {
            listenerCallback = listenerCallback.bind(this.options.bind);
        }

        element[this.options.add](
            event,
            listenerCallback
        );

        if (typeof this.events[elementIndex] !== 'object') {
            this.events[elementIndex] = {};
        }

        if (typeof this.events[elementIndex][event] !== 'object') {
            this.events[elementIndex][event] = [];
        }

        this.events[elementIndex][event].push({
            callback,
            // We must store bound and original callback so that we may remove the listener later
            bound: this.options.bind ? listenerCallback : null
        });
    }
    
    /**
     * Get the index event object inside the given event stack by the original callback passed to this.on
     *
     * @param eventStack - The particular stack of event objects from a certain event type.
     * @param callback - The original callback passed to this.on
     */
    getIndexOfEventObjectByCallback(eventStack, callback) {
        let matchingIndex = -1;

        eventStack.forEach((eventObject, index) => {
            if (matchingIndex === -1 && eventObject.callback === callback) {
                matchingIndex = index;
            }
        });

        return matchingIndex;
    }

    /**
     * Get the event object inside this.events by the original callback passed.
     * 
     * @param eventStack - The particular stack of event objects from a certain event type.
     * @param callback - The original callback passed to this.on
     */
    getEventObjectByCallback(eventStack, callback) {
        let index = this.getIndexOfEventObjectByCallback(eventStack, callback),
            match = null;
        
        if (index !== -1) {
            match = eventStack[index];
        }
        
        return match;
    }

    /**
     * Removes event listeners that were set on this element using this instance of EventManager.
     *
     * This method supports all possible combinations of the arguments to this function.
     *
     * For instance, just passing the callback as the third argument, while the others remain null, will result in all
     * events on all elements that use the specified callback, that were set with this instance of EventManager, being
     * removed.
     *
     * Or, if just the event is passed, with the element and callback null, all events set with this instance of
     * EventManager, of that type will be removed.
     *
     * @param {*} element - The element to remove listeners from, pass null remove listeners from all elements
     * @param {string|null} event - The event type to remove, pass null to remove listeners of all event types
     * @param {function|null} callback - The event listener remvoe, pass null to remove all listeners
     */
    off(element = null, event = null, callback = null) {
        // Validate element
        if (element !== null && typeof element !== 'object' && typeof element[this.options.remove] !== 'function') {
            throw new TypeError('Invalid element passed to EventManager.off');
        }

        // Validate event type
        if (event !== null && typeof event !== 'string') {
            throw new TypeError('Invalid event passed to EventManager.off: ' + event);
        }

        // Validate callback
        if (callback !== null && typeof callback !== 'function') {
            throw new TypeError('Invalid callback passed to EventManager.off');
        }

        // Remove all listeners
        if ( ! element && ! event && ! callback) { // 000
            this.elements.forEach(element => this.off(element));
        }

        // Remove all listeners of all event types of a given callback from all elements
        else if ( ! element && ! event && callback) { // 001
            this.elements.forEach(element => this.off(element, event, callback));
        }

        // Remove all listeners of a given event type from all elements
        else if ( ! element && event && ! callback) { // 010
            this.elements.forEach(element => this.off(element, event, callback));
        }

        // Remove all listeners of a given event type and callback from all elements
        else if ( ! element && event && callback) { // 011
            this.elements.forEach(element => this.off(element, event, callback));
        }

        // Remove all listeners for the given element
        else if (element && ! event && ! callback) { // 100
            let index = this.getIndex(element);

            if (typeof this.events[index] !== 'object') return;

            let event,
                elementEvents = this.events[index];

            for (event in elementEvents) {
                if (elementEvents.hasOwnProperty(event)) {
                    this.off(element, event);
                }
            }
        }

        // Remove all listeners of all event types of a given callback for a given element
        else if (element && ! event && callback) { // 101
            let index = this.getIndex(element);

            if (typeof this.events[index] !== 'object') return;

            let event,
                elementEvents = this.events[index];

            for (event in elementEvents) {
                if (elementEvents.hasOwnProperty(event)) {
                    let eventObject = this.getEventObjectByCallback(elementEvents[event], callback);
                    
                    // If event object exists with matching callback is found, go ahead and remove it, otherwise, do
                    // nothing, because no listener was set on the given element with the given callback for this
                    // particular event type.
                    if (eventObject) {
                        this.off(element, event, callback);
                    }
                }
            }
        }

        // Remove all listeners of given type for a given element
        else if (element && event && ! callback) { // 110
            let index = this.getIndex(element);

            if (
                typeof this.events[index] !== 'object' &&
                typeof this.events[index][event] !== 'object'
            ) return;

            this.events[index][event].forEach(
                eventObject => this.off(element, event, eventObject.callback)
            );
        }

        // Remove given listener of given type for a given element
        else if (element && event && callback) { // 111
            let index = this.getIndex(element);

            // Attempt to remove it, even if below conditions mean it wasn't ever set with this class instance
            element[this.options.remove](event, callback);
            
            // If event matching given element and event type is found, look for the given callback inside of it
            if (this.events[index] && this.events[index][event]) {
                let eventObjectIndex = this.getIndexOfEventObjectByCallback(this.events[index][event], callback);
                
                // If not equal to -1, we have a match
                if (eventObjectIndex !== -1) {
                    // Remove the event
                    let eventObject = this.events[index][event][eventObjectIndex];
                    
                    // Remove the event listner
                    element[this.options.remove](
                        event,
                        
                        // Use either the bound listener of the original callback depending on config
                        this.options.bind
                            ? eventObject.bound
                            : eventObject.callback
                    );

                    // Then remove the event object from the stack
                    this.events[index][event].splice(eventObjectIndex, 1);
                }
            }
        }

        // This should never happen since every combination of the arguments has been handled above
        else {
            throw new Error('Invalid arguments passed to EventManager.off');
        }
    }

    /**
     * Destroy this instance and make it unusable from this point forth to avoid accidental usage.
     */
    destroy() {
        this.off();
        this.events = {};
        this.elements = [];
        this.destroyed = true;

        this.getIndex = this.on = this.off = this.destroy = null;
    }
}